import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { aiApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Sparkles, Image, Download, Loader2, Info, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';

const Illustrations = ({ onBack, onNavigate }) => {
    const { user, refreshUser } = useAuth();
    const [prompt, setPrompt] = useState('');
    const [style, setStyle] = useState('digital art, book illustration');
    const [tier, setTier] = useState('standard');
    const [loading, setLoading] = useState(false);
    const [illustrations, setIllustrations] = useState([]);
    const [currentImage, setCurrentImage] = useState(null);

    useEffect(() => {
        loadIllustrations();
    }, []);

    const loadIllustrations = async () => {
        try {
            const response = await aiApi.illustrations();
            setIllustrations(response.data);
        } catch (error) {
            console.error('Failed to load illustrations');
        }
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await aiApi.illustrate({ prompt, style, tier });
            setCurrentImage(response.data);
            setIllustrations([response.data, ...illustrations]);
            refreshUser();
            toast.success(`Illustration created! Used ${response.data.credits_used} credits.`);
        } catch (error) {
            if (error.response?.status === 402) {
                toast.error('Insufficient credits. Please purchase more.');
            } else {
                toast.error('Failed to generate illustration');
            }
        } finally {
            setLoading(false);
        }
    };

    const stylePresets = [
        { name: 'Book Illustration', value: 'digital art, book illustration, detailed' },
        { name: 'Fantasy Art', value: 'fantasy art, epic, magical, detailed illustration' },
        { name: 'Realistic Portrait', value: 'realistic portrait, detailed, professional photography style' },
        { name: 'Watercolor', value: 'watercolor painting, soft colors, artistic' },
        { name: 'Manga/Anime', value: 'anime style, manga illustration, detailed' },
        { name: 'Dark & Moody', value: 'dark fantasy, moody atmosphere, dramatic lighting' },
    ];

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={onBack} data-testid="back-btn">
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back
                        </Button>
                        <h1 className="font-serif text-2xl font-semibold">AI Illustrations</h1>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-accent" />
                            <span className="font-medium">{user?.credits || 0} credits</span>
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => onNavigate && onNavigate('credits')}
                            data-testid="illustrations-buy-credits"
                        >
                            Buy Credits
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                <div className="grid lg:grid-cols-2 gap-8">
                    {/* Generator Panel */}
                    <div>
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-serif">Generate Illustration</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleGenerate} className="space-y-6">
                                    {/* Tier Selection */}
                                    <div className="space-y-3">
                                        <Label>Quality Tier</Label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                className={`p-4 rounded-lg border text-left transition-colors ${
                                                    tier === 'standard' 
                                                        ? 'border-accent bg-accent/5' 
                                                        : 'border-border hover:border-muted-foreground'
                                                }`}
                                                onClick={() => setTier('standard')}
                                                data-testid="tier-standard"
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-semibold">Standard</span>
                                                    <Badge variant="secondary">5 credits</Badge>
                                                </div>
                                                <p className="text-xs text-muted-foreground">
                                                    Powered by fal.ai Flux. Great for concepts and drafts.
                                                </p>
                                            </button>
                                            <button
                                                type="button"
                                                className={`p-4 rounded-lg border text-left transition-colors ${
                                                    tier === 'premium' 
                                                        ? 'border-accent bg-accent/5' 
                                                        : 'border-border hover:border-muted-foreground'
                                                }`}
                                                onClick={() => setTier('premium')}
                                                data-testid="tier-premium"
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-semibold">Premium</span>
                                                    <Badge>10 credits</Badge>
                                                </div>
                                                <p className="text-xs text-muted-foreground">
                                                    Powered by OpenAI GPT Image 1. Best quality for final art.
                                                </p>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Prompt */}
                                    <div className="space-y-2">
                                        <Label htmlFor="prompt">Describe your illustration</Label>
                                        <Textarea
                                            id="prompt"
                                            value={prompt}
                                            onChange={(e) => setPrompt(e.target.value)}
                                            placeholder="A wise old wizard with a long silver beard, standing in an ancient library filled with glowing magical books..."
                                            rows={4}
                                            required
                                            data-testid="illustration-prompt"
                                        />
                                    </div>

                                    {/* Style Presets */}
                                    <div className="space-y-2">
                                        <Label>Style Preset</Label>
                                        <div className="flex flex-wrap gap-2">
                                            {stylePresets.map((preset) => (
                                                <Badge
                                                    key={preset.name}
                                                    variant={style === preset.value ? "default" : "outline"}
                                                    className="cursor-pointer"
                                                    onClick={() => setStyle(preset.value)}
                                                >
                                                    {preset.name}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Custom Style */}
                                    <div className="space-y-2">
                                        <Label htmlFor="style">Custom Style (optional)</Label>
                                        <Input
                                            id="style"
                                            value={style}
                                            onChange={(e) => setStyle(e.target.value)}
                                            placeholder="digital art, fantasy, detailed..."
                                            data-testid="illustration-style"
                                        />
                                    </div>

                                    {/* Info Box */}
                                    <div className="bg-muted/50 rounded-lg p-4 flex gap-3">
                                        <Info className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-muted-foreground">
                                            <p className="font-medium text-foreground mb-1">Consistent Book Styling</p>
                                            <p>Use the same style description across illustrations to maintain visual consistency throughout your book.</p>
                                        </div>
                                    </div>

                                    <Button 
                                        type="submit" 
                                        className="w-full btn-primary"
                                        disabled={loading || !prompt.trim()}
                                        data-testid="generate-btn"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="h-4 w-4 mr-2" />
                                                Generate ({tier === 'premium' ? 10 : 5} credits)
                                            </>
                                        )}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Preview & Gallery */}
                    <div className="space-y-6">
                        {/* Current Image Preview */}
                        {currentImage && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-serif text-lg">Latest Generation</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="aspect-square rounded-lg overflow-hidden bg-muted mb-4">
                                        <img 
                                            src={currentImage.image_url} 
                                            alt="Generated illustration"
                                            className="w-full h-full object-cover"
                                            data-testid="current-image"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <Badge variant="outline">{currentImage.tier}</Badge>
                                        <a 
                                            href={currentImage.image_url} 
                                            download="illustration.png"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <Button variant="outline" size="sm">
                                                <Download className="h-4 w-4 mr-2" />
                                                Download
                                            </Button>
                                        </a>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Gallery */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-serif text-lg">Your Gallery</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {illustrations.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Image className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                                        <p className="text-muted-foreground">No illustrations yet</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-3 gap-3">
                                        {illustrations.map((img) => (
                                            <div 
                                                key={img.illustration_id || img.id}
                                                className="aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-90 transition-opacity"
                                                onClick={() => setCurrentImage(img)}
                                                data-testid={`gallery-image-${img.illustration_id || img.id}`}
                                            >
                                                <img 
                                                    src={img.image_url} 
                                                    alt="Illustration"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Illustrations;
